import { BillableIcon } from "@/features/attorney/views/Entries/BillableIcon";
import { toYYYYMMDD } from "@/features/attorney/views/Entries/entryFilter";
import type {
	Entry,
	EntryStatus,
} from "@/features/attorney/views/Entries/normalize";
import { StatusIcon } from "@/features/attorney/views/Entries/StatusIcon";
import {
	type CalendarDayContext,
	CalendarDayHeading,
	type CalendarWeekContext,
} from "./CalendarGrid";

/**
 * Per-day hour sums for one date: the filtered grand total plus per-status and
 * per-billability buckets. Built by Calendar2View from the filtered entry set
 * (minus the date window) and looked up per cell by `YYYY-MM-DD`.
 */
export type CalendarDaySums = {
	total: number;
	status: Map<string, number>;
	billable: Map<string, number>;
};

type BillableCategory = Entry["billableCategory"];

// Canonical render order so breakdown rows are stable regardless of the order
// values were toggled into the filter chip.
const STATUS_ORDER: EntryStatus[] = [
	"tracked",
	"reviewed",
	"exported",
	"deleted",
];
const BILLABLE_ORDER: BillableCategory[] = [
	"billable",
	"non_billable",
	"unknown",
];

function fmtHours(h: number): string {
	return `${h.toFixed(1)}h`;
}

type BreakdownRow<T> = { value: T; hours: number };

// One row per selected filter value that actually has time, in canonical order.
// Returns [] when the filter isn't active or none of the selected values have
// time, which is the signal to render no breakdown column for that dimension.
function statusRows(
	values: string[] | null | undefined,
	bucket: Map<string, number> | undefined,
): BreakdownRow<EntryStatus>[] {
	if (!values || values.length === 0) return [];
	return STATUS_ORDER.flatMap((s) => {
		if (!values.includes(s)) return [];
		const hours = bucket?.get(s) ?? 0;
		return hours > 0 ? [{ value: s, hours }] : [];
	});
}

function billableRows(
	values: string[] | null | undefined,
	bucket: Map<string, number> | undefined,
): BreakdownRow<BillableCategory>[] {
	if (!values || values.length === 0) return [];
	return BILLABLE_ORDER.flatMap((b) => {
		if (!values.includes(b)) return [];
		const hours = bucket?.get(b) ?? 0;
		return hours > 0 ? [{ value: b, hours }] : [];
	});
}

// Sum a set of days into one aggregate (used by the week cell).
function aggregate(
	days: Date[],
	sumsByDay: Map<string, CalendarDaySums> | undefined,
): CalendarDaySums {
	const out: CalendarDaySums = {
		total: 0,
		status: new Map(),
		billable: new Map(),
	};
	if (!sumsByDay) return out;
	for (const d of days) {
		const a = sumsByDay.get(toYYYYMMDD(d));
		if (!a) continue;
		out.total += a.total;
		for (const [k, v] of a.status)
			out.status.set(k, (out.status.get(k) ?? 0) + v);
		for (const [k, v] of a.billable)
			out.billable.set(k, (out.billable.get(k) ?? 0) + v);
	}
	return out;
}

/** Two stacked icon+hours columns: statuses on the left, billability on the
 * right. Renders nothing when both are empty (no relevant filter active). */
function Breakdown({
	status,
	billable,
}: {
	status: BreakdownRow<EntryStatus>[];
	billable: BreakdownRow<BillableCategory>[];
}) {
	if (status.length === 0 && billable.length === 0) return null;
	const rowCls =
		"flex items-center gap-1 text-[11px] leading-none tabular-nums text-muted-foreground sm:text-xs";
	return (
		<div className="mt-auto flex items-start gap-2 pt-1">
			{status.length > 0 && (
				<div className="flex flex-col gap-0.5">
					{status.map((r) => (
						<span key={r.value} className={rowCls}>
							<StatusIcon status={r.value} className="size-3.5" />
							{fmtHours(r.hours)}
						</span>
					))}
				</div>
			)}
			{billable.length > 0 && (
				<div className="flex flex-col gap-0.5">
					{billable.map((r) => (
						<span key={r.value} className={rowCls}>
							<BillableIcon category={r.value} className="size-3.5" />
							{fmtHours(r.hours)}
						</span>
					))}
				</div>
			)}
		</div>
	);
}

interface CellProps {
	/** Selected status values (operator "is"), or null when no status filter. */
	statusValues?: string[] | null;
	/** Selected billability values (operator "is"), or null when none. */
	billableValues?: string[] | null;
}

/**
 * Day-slot content: the date heading with the filtered grand total to its
 * right, plus the status/billability breakdown below when those filters are
 * active. Drop into CalendarGrid's `renderDay`.
 */
export function DayHoursCell({
	ctx,
	sums,
	statusValues,
	billableValues,
}: CellProps & {
	ctx: CalendarDayContext;
	sums: CalendarDaySums | undefined;
}) {
	const total = sums?.total ?? 0;
	return (
		<>
			<div className="flex items-center justify-between gap-1">
				<CalendarDayHeading ctx={ctx} />
				{total > 0 && (
					<span className="shrink-0 text-[10px] font-medium leading-none tabular-nums text-foreground">
						{fmtHours(total)}
					</span>
				)}
			</div>
			{/* Breakdown only renders values that actually have time. */}
			{total > 0 && (
				<Breakdown
					status={statusRows(statusValues, sums?.status)}
					billable={billableRows(billableValues, sums?.billable)}
				/>
			)}
		</>
	);
}

/**
 * Week-slot content: the "W##" label with the week's filtered total beside it,
 * plus the same breakdown below. Drop into CalendarGrid's `renderWeek`.
 */
export function WeekHoursCell({
	ctx,
	sumsByDay,
	statusValues,
	billableValues,
}: CellProps & {
	ctx: CalendarWeekContext;
	sumsByDay: Map<string, CalendarDaySums> | undefined;
}) {
	const week = aggregate(ctx.days, sumsByDay);
	return (
		<>
			<div className="flex items-center gap-1">
				<span className="text-[10px] font-medium text-muted-foreground/60">
					W{ctx.weekNumber}
				</span>
				{week.total > 0 && (
					<span className="text-[10px] font-medium tabular-nums text-muted-foreground">
						{fmtHours(week.total)}
					</span>
				)}
			</div>
			{week.total > 0 && (
				<Breakdown
					status={statusRows(statusValues, week.status)}
					billable={billableRows(billableValues, week.billable)}
				/>
			)}
		</>
	);
}
